import { observer } from "mobx-react";
import React, { useState, useEffect } from "react";
import { Cubby } from "../stores/Cubby";
import { Affix, rem, Notification, Switch, Box, Portal } from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconHourglass,
  IconFlag,
  IconClock,
} from "@tabler/icons-react";
import { useViewportSize } from "@mantine/hooks";

const Tasks = observer(({ cubby }: { cubby: Cubby }) => {
  const { width } = useViewportSize();
  const isMobile = width < 768; // Adjust this breakpoint as needed

  const [isLargeSize, setIsLargeSize] = useState(() => {
    // Check localStorage for a saved value
    const saved = localStorage.getItem("notificationSize");
    if (saved !== null) {
      return JSON.parse(saved); // Use the saved value
    }
    // Default to true for non-mobile devices
    const isMobile = window.innerWidth < 768; // Check directly at initialization
    return !isMobile;
  });

  useEffect(() => {
    // Always store the updated size in localStorage
    localStorage.setItem("notificationSize", JSON.stringify(isLargeSize));
  }, [isLargeSize]);

  const getIconSize = () => rem(isLargeSize ? 44 : 16);

  // Define icons
  const xIcon = (
    <IconX style={{ width: getIconSize(), height: getIconSize() }} />
  );
  const checkIcon = (
    <IconCheck style={{ width: getIconSize(), height: getIconSize() }} />
  );
  const hourglassIcon = (
    <IconHourglass style={{ width: getIconSize(), height: getIconSize() }} />
  );
  const flagIcon = (
    <IconFlag style={{ width: getIconSize(), height: getIconSize() }} />
  );
  const clockIcon = (
    <IconClock style={{ width: getIconSize(), height: getIconSize() }} />
  );

  // Function to determine icon and color
  const getIconAndColor = (status: string, kind: string) => {
    switch (status) {
      case "initialized":
        return { icon: hourglassIcon, color: "yellow", loading: false };
      case "starting":
        return { icon: flagIcon, color: "gray", loading: false };
      case "in_progress":
        return { icon: hourglassIcon, color: "blue", loading: true };
      case "completed":
        if (kind === "onchain_transaction_pending") {
          return { icon: clockIcon, color: "blue", loading: false };
        } else {
          return { icon: checkIcon, color: "green", loading: false };
        }
      case "errored":
        return { icon: xIcon, color: "red", loading: false };
      default:
        return { icon: hourglassIcon, color: "gray", loading: false };
    }
  };

  // Notification styles
  const getNotificationStyles = () => ({
    root: {
      position: "relative" as const,
      zIndex: 10000,
      marginBottom: rem(30),
      ...(isLargeSize && { padding: "calc(1.2rem * var(--mantine-scale))" }),
    },
    title: {
      ...(isLargeSize
        ? {
            fontSize: rem(24),
            marginBottom: rem(8),
          }
        : {}),
    },
    description: {
      ...(isLargeSize
        ? {
            fontSize: rem(20),
          }
        : {}),
    },
    icon: {
      ...(isLargeSize
        ? {
            width: rem(48),
            height: rem(48),
            padding: rem(10),
            borderRadius: "50%",
            marginRight: rem(0),
          }
        : {}),
    },
    body: {
      ...(isLargeSize
        ? {
            paddingLeft: rem(20),
          }
        : {}),
    },
  });

  const tasks = cubby.tasksToShowToUser;

  return (
    <Portal>
      <Affix
        position={{ bottom: rem(20), right: rem(20) }}
        style={{ zIndex: 10000 }}
      >
        {tasks.map((task, index) => {
          const { icon, color, loading } = getIconAndColor(
            task.lifecycleStatus,
            task.kind
          );
          return (
            <Box
              key={task.uuid}
              style={{
                position: "relative",
                marginBottom: rem(10),
                zIndex: 10000,
              }}
            >
              <Notification
                withCloseButton={false}
                loading={loading}
                icon={icon}
                color={color}
                title={task.humanReadableKind}
                styles={getNotificationStyles()}
              >
                <Box>{task.humanReadableLifecycleStatus}</Box>
              </Notification>

              {/* Show Switch only below the first notification and when not mobile */}
              {!isMobile && index === tasks.length - 1 && (
                <Box
                  style={{
                    position: "absolute",
                    bottom: rem(-25),
                    right: rem(5),
                    display: "flex",
                    alignItems: "center",
                    gap: rem(5),
                    fontSize: isLargeSize ? rem(14) : rem(10),
                  }}
                >
                  <Switch
                    label={isLargeSize ? "big" : "small"}
                    checked={isLargeSize}
                    onChange={(event) =>
                      setIsLargeSize(event.currentTarget.checked)
                    }
                    size="xs"
                    color="gray"
                    styles={{
                      root: {
                        opacity: 0.25,
                      },
                      label: {
                        opacity: 0.7,
                      },
                      labelWrapper: {
                        paddingLeft: rem(0),
                        marginLeft: rem(0),
                      },
                      body: {
                        margin: 0,
                        paddingLeft: 0,
                      },
                    }}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Affix>
    </Portal>
  );
});

export default Tasks;
